import React, { FC, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { AppActions, Copy, ThemeProps } from '../../../types';
import { X } from 'phosphor-react';
import { Button, Markdown } from '../..';

type PopupType = 'error' | 'success' | 'warning';
export enum ErrorKeys {
  GENERIC_ERROR = 'GENERIC_ERROR',
  ERROR_MESSAGE = 'ERROR_MESSAGE',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_VOUCHER_CODE = 'INVALID_VOUCHER_CODE',
}

export type Message = {
  /**
   * is the message dismissable
   */
  dismissable: boolean;
  /**
   * The id of the error, used for dismmissing
   */
  id: number;
  /**
   * message for the error mapper
   */
  text: string;
  /**
   * Number of seconds to keep the message. Default 7 secs
   */
  timeout?: number;
  /**
   * The type of error to notify to the user
   */
  type: PopupType;
};

interface PopupProps extends Message {
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * functin to remove meesage from the stack
   */
  dismissMessage: (id: number) => void;
}

const Popup: FC<PopupProps> = ({
  className,
  dismissable,
  dismissMessage,
  id,
  text,
  timeout,
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null | undefined>();

  useEffect(() => {
    if (timeout) {
      timeoutRef.current = setTimeout(function () {
        dismissMessage(id);
      }, timeout * 1000);
    }

    return () => {
      timeoutRef.current = null;
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [dismissMessage, id, timeout]);

  return (
    <div className={className}>
      <Markdown className='text'>{text}</Markdown>
      {dismissable && (
        <Button onClick={() => dismissMessage(id!)} variant='transparent'>
          <X size={24} />
        </Button>
      )}
    </div>
  );
};

const StyledPopup = styled((props: any) => <Popup {...props} />)`
  ${({ theme, type }: { theme: ThemeProps; type: PopupType }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors[type],
    color:
      type === 'warning' ? theme.colors.text.primary : theme.colors.text.white,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    padding: '8px 10px',
    width: '100%',
    button: {
      height: 'auto',
      minWidth: 24,
      padding: 0,
    },
    '.text': {
      marginRight: 10,
    },
    svg: {
      color:
        type === 'warning'
          ? theme.colors.text.primary
          : theme.colors.text.white,
    },
  })}
`;

interface NotificationProps extends Copy {
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * Send actions to state context
   */
  dispatch: (action: AppActions) => void;
  /**
   * The message to display to the user
   */
  messages: Message[];
}

const Notification: FC<NotificationProps> = ({
  className,
  dispatch,
  messages,
}) => {
  if (!(messages || []).length) return null;

  const dismissMessage = (id: number) => {
    dispatch({
      type: 'DELETE_MESSAGE',
      payload: id,
    });
  };

  return (
    <div className={className}>
      {messages.map((message: Message) => (
        <StyledPopup
          key={message.id}
          {...message}
          dismissMessage={dismissMessage}
        />
      ))}
    </div>
  );
};

const StyledNotification = styled((props: any) => <Notification {...props} />)`
  ${({ theme }: { theme: ThemeProps }) => ({
    bottom: 0,
    maxWidth: 400,
    minWidth: 250,
    padding: 15,
    position: 'fixed',
    right: 0,
  })}
`;

export default StyledNotification;
